/**
 * 全站统一 loading 视觉。
 *
 * 三个变体：
 * - `spinner`（默认）：旋转 spinner + 标签。短任务用，~1s 内完成的请求。
 * - `skeleton`：脉冲灰块阵列。中等任务用（数据已加载中、骨架占位）。
 * - `wizard-skeleton`：Wizard 入口专用——两栏骨架 + 单行说明。
 *
 * 历史教训：曾经用 setInterval 轮播 ['正在同步飞书', '加载草稿', '准备就绪']
 * 假阶段。前端并没有真进度（单次 getOnboardingDraft 内部做反向 sync），
 * 轮播看起来像死循环；尤其"准备就绪"暗示完成后又跳回起点。
 * 现在统一只显示一条诚实文案 + spinner 表示"还在动"，调用方可通过 stage
 * 传入更具体的进度文本（如 commit 阶段）。
 */

export interface LoadingProps {
  /** 视觉变体 */
  variant?: 'spinner' | 'skeleton' | 'wizard-skeleton';
  /** 单行说明文本（spinner / skeleton 顶部）。wizard-skeleton 不用，自己有默认文案。 */
  label?: string;
  /** Wizard skeleton 用：覆盖默认文案（调用方有真进度时用）。不传走默认单条文案。 */
  stage?: string;
  /** className 附加在外层 */
  className?: string;
}

const SPINNER_SVG = (
  <svg
    className="h-4 w-4 shrink-0 animate-spin text-slate-500"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

function Spinner({ label, className }: { label?: string; className?: string }): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="loading-spinner"
      className={`flex items-center gap-2 text-sm text-slate-500 ${className ?? ''}`}
    >
      {SPINNER_SVG}
      <span>{label ?? '加载中…'}</span>
    </div>
  );
}

function Skeleton({ label, className }: { label?: string; className?: string }): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="loading-skeleton"
      className={`space-y-3 ${className ?? ''}`}
    >
      {label && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          {SPINNER_SVG}
          <span>{label}</span>
        </div>
      )}
      <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
      <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
      <div className="h-4 w-5/6 animate-pulse rounded bg-slate-200" />
    </div>
  );
}

const WIZARD_DEFAULT_STAGE = '正在从飞书同步并加载草稿…';

/**
 * Wizard 入口骨架屏。
 * 长 loading（最多 ~10s 飞书 round-trip + 草稿 fetch）期间：
 * 1. 顶部一条诚实文案 + spinner（不再假装有多阶段进度）
 * 2. 双栏骨架（左：步骤指示 / 右：表单）模拟最终 layout，切换时不视觉跳跃
 * 3. spinner 永动表达"还在动"
 *
 * 调用方有真阶段（如 commit 时的多步流程）可传 stage 覆盖默认文案。
 */
function WizardSkeleton({ stage, className }: { stage?: string; className?: string }): JSX.Element {
  const text = stage ?? WIZARD_DEFAULT_STAGE;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="loading-wizard-skeleton"
      className={`space-y-6 ${className ?? ''}`}
    >
      <div className="flex items-center gap-2.5 rounded-lg border border-sky-200 bg-gradient-to-r from-sky-50 to-indigo-50 px-4 py-3">
        <svg
          className="h-5 w-5 shrink-0 animate-spin text-sky-600"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <div className="flex-1">
          <div className="text-sm font-medium text-sky-900">向导加载中</div>
          <div
            data-testid="wizard-stage-text"
            className="mt-0.5 text-xs text-sky-700/80 transition-opacity"
          >
            {text}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[14rem_1fr]">
        {/* 步骤指示骨架 */}
        <div className="space-y-2">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-2 w-2 animate-pulse rounded-full bg-slate-300" />
              <div
                className="h-3 animate-pulse rounded bg-slate-200"
                style={{ width: `${50 + ((i * 13) % 40)}%` }}
              />
            </div>
          ))}
        </div>

        {/* 表单骨架 */}
        <div className="space-y-4">
          <div className="h-6 w-1/3 animate-pulse rounded bg-slate-200" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
          <div className="space-y-2">
            <div className="h-3 w-20 animate-pulse rounded bg-slate-200" />
            <div className="h-9 w-full animate-pulse rounded border border-slate-200 bg-slate-50" />
          </div>
          <div className="space-y-2">
            <div className="h-3 w-24 animate-pulse rounded bg-slate-200" />
            <div className="h-9 w-full animate-pulse rounded border border-slate-200 bg-slate-50" />
          </div>
          <div className="space-y-2">
            <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
            <div className="h-24 w-full animate-pulse rounded border border-slate-200 bg-slate-50" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Loading({
  variant = 'spinner',
  label,
  stage,
  className,
}: LoadingProps): JSX.Element {
  if (variant === 'wizard-skeleton') {
    return (
      <WizardSkeleton
        {...(stage !== undefined ? { stage } : {})}
        {...(className !== undefined ? { className } : {})}
      />
    );
  }
  if (variant === 'skeleton') {
    return (
      <Skeleton
        {...(label !== undefined ? { label } : {})}
        {...(className !== undefined ? { className } : {})}
      />
    );
  }
  return (
    <Spinner
      {...(label !== undefined ? { label } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}
